import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, Search, X, Phone, MessageCircle, Home as HomeIcon, MapPin, LogIn, User, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";

const NAV = [
  { to: "/dhams", key: "nav_dhams", label: "Dhams" },
  { to: "/temples", key: "nav_temples", label: "Temples" },
  { to: "/yatras", key: "nav_yatras", label: "Yatras" },
  { to: "/packages", key: "nav_packages", label: "Packages" },
  { to: "/hotels", key: "nav_hotels", label: "Hotels" },
  { to: "/dharamshalas", key: "nav_dharamshalas", label: "Dharamshalas" },
  { to: "/guides", key: "nav_guides", label: "Guides" },
];

export default function Layout() {
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useLang();

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div className="min-h-screen mandala-bg">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-offwhite/90 backdrop-blur-md border-b border-gold/20">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0">
            <div className="w-9 h-9 rounded-full bg-indigo-deep flex items-center justify-center">
              <span className="font-display text-gold text-lg">॥</span>
            </div>
            <div className="hidden sm:block">
              <div className="font-display text-lg font-bold text-indigo-deep leading-none">IndiaDham</div>
              <div className="text-[10px] tracking-widest text-gold-dark uppercase">{t("tagline")}</div>
            </div>
          </Link>

          <form onSubmit={onSearch} className="hidden md:flex flex-1 max-w-lg mx-6 relative">
            <input
              data-testid="header-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("search_placeholder")}
              className="w-full h-10 pl-10 pr-4 rounded-full border border-gold/30 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-gold/50"
            />
            <Search className="absolute left-3 top-2.5 w-5 h-5 text-indigo-deep/60" />
          </form>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium rounded-md transition-colors ${isActive ? "text-gold-dark bg-gold/10" : "text-indigo-deep/80 hover:text-gold-dark hover:bg-gold/5"}`
                }
              >
                {t(n.key)}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button type="button" data-testid="lang-toggle" onClick={() => setLang(lang === "en" ? "hi" : "en")} className="h-8 px-2.5 rounded-full border border-gold/40 text-xs font-semibold text-indigo-deep hover:bg-gold/10 flex items-center gap-1">
              <Globe className="w-3.5 h-3.5 text-gold-dark" />{t("lang_toggle")}
            </button>
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link to="/admin" data-testid="admin-link">
                    <Button variant="outline" size="sm" className="hidden sm:inline-flex border-gold/40 text-indigo-deep hover:bg-gold hover:text-white">{t("admin")}</Button>
                  </Link>
                )}
                <Link to="/account" data-testid="account-link">
                  <Button variant="ghost" size="sm" className="text-indigo-deep hover:bg-gold/10">
                    <User className="w-4 h-4 mr-1" /> {user.name?.split(" ")[0] || t("account")}
                  </Button>
                </Link>
                <Button data-testid="logout-btn" onClick={logout} variant="ghost" size="sm" className="hidden sm:inline-flex">{t("logout")}</Button>
              </>
            ) : (
              <Link to="/auth" data-testid="login-link">
                <Button size="sm" className="bg-indigo-deep hover:bg-indigo text-white">
                  <LogIn className="w-4 h-4 mr-1" /> {t("sign_in")}
                </Button>
              </Link>
            )}

            {/* Mobile menu */}
            <Sheet>
              <SheetTrigger asChild>
                <Button data-testid="mobile-menu-btn" variant="ghost" size="icon" className="lg:hidden">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 bg-offwhite">
                <div className="pt-6 space-y-2">
                  <form onSubmit={onSearch} className="relative mb-4">
                    <input
                      data-testid="mobile-search-input"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Search…"
                      className="w-full h-10 pl-9 pr-3 rounded-lg border border-gold/30"
                    />
                    <Search className="absolute left-3 top-2.5 w-4 h-4 text-indigo-deep/60" />
                  </form>
                  {NAV.map((n) => (
                    <Link key={n.to} to={n.to} className="block px-3 py-2.5 rounded-md text-indigo-deep hover:bg-gold/10 font-medium">
                      {t(n.key)}
                    </Link>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="pb-24 lg:pb-6">
        <Outlet />
      </main>

      {/* Mobile bottom action bar */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gold/20" style={{ boxShadow: "0 -4px 25px rgba(16,20,42,0.12)" }}>
        <div className="grid grid-cols-4 py-2">
          <Link to="/" data-testid="mobile-nav-home" className="flex flex-col items-center gap-0.5 text-indigo-deep">
            <HomeIcon className="w-5 h-5" /><span className="text-[11px]">{t("home")}</span>
          </Link>
          <Link to="/yatras" data-testid="mobile-nav-yatras" className="flex flex-col items-center gap-0.5 text-indigo-deep">
            <MapPin className="w-5 h-5" /><span className="text-[11px]">{t("nav_yatras")}</span>
          </Link>
          <a href="tel:+919999999999" data-testid="mobile-nav-call" className="flex flex-col items-center gap-0.5 text-saffron">
            <Phone className="w-5 h-5" /><span className="text-[11px]">{t("call")}</span>
          </a>
          <a href="https://wa.me/919999999999" target="_blank" rel="noreferrer" data-testid="mobile-nav-whatsapp" className="flex flex-col items-center gap-0.5" style={{ color: "#25D366" }}>
            <MessageCircle className="w-5 h-5" /><span className="text-[11px]">{t("whatsapp")}</span>
          </a>
        </div>
      </div>

      <footer className="hidden lg:block border-t border-gold/20 bg-indigo-deep text-offwhite/80 mt-8">
        <div className="max-w-7xl mx-auto px-6 py-10 grid md:grid-cols-4 gap-8 text-sm">
          <div>
            <div className="font-display text-xl text-gold mb-3">IndiaDham</div>
            <p className="text-offwhite/60">Sacred journeys. Seamless blessings. India's premier religious travel & pilgrimage portal.</p>
          </div>
          <div>
            <div className="font-semibold mb-3 text-white">Explore</div>
            <ul className="space-y-1.5">
              {NAV.map(n => <li key={n.to}><Link to={n.to} className="hover:text-gold">{t(n.key)}</Link></li>)}
            </ul>
          </div>
          <div>
            <div className="font-semibold mb-3 text-white">Support</div>
            <ul className="space-y-1.5">
              <li>Help Center</li><li>Cancellation Policy</li><li>Terms & Privacy</li>
            </ul>
          </div>
          <div>
            <div className="font-semibold mb-3 text-white">Get in touch</div>
            <p>24×7 Yatra Helpline</p>
            <p className="text-gold">+91 99999 99999</p>
            <p className="mt-2">support@indiadham.com</p>
          </div>
        </div>
        <div className="border-t border-white/10 py-4 text-center text-xs text-offwhite/50">© 2026 IndiaDham.com — Har Har Mahadev 🕉</div>
      </footer>
    </div>
  );
}
